import {
  BaseCollectiveQueryResult,
  EventFieldsFragment,
  SignupMutationResult,
  useSignupMutation
} from 'graphql/generated'
import useControlledInput from 'hooks/useControlledInput'
import useLogin from 'hooks/useLogin'
import { parseGqlErrors } from 'hooks/useQueryErrorHandler'
import { ROOT_HOSTNAME } from 'lib/helpers'
import { useState, useCallback } from 'react'

interface IWalletProps {
  address: string
  signature: string
  ephemeralCryptoAuthId: string
}

export default function (
  event?: EventFieldsFragment,
  collective?: Maybe<BaseCollectiveQueryResult['collective']>,
  wallet?: IWalletProps
) {
  const [email, setEmail] = useControlledInput()
  const [password, setPassword] = useControlledInput()
  const [username, setUsername, , setRawUsername] = useControlledInput()

  const [loggingIn, setLoggingIn] = useState(false)

  const deviceInfo = {
    appVersion: '1.0',
    systemVersion: 'web',
    timezone:
      typeof window !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined
  }

  const doLogin = useLogin()

  const onAfterLogin = useCallback(
    async (resp: SignupMutationResult) => {
      setLoggingIn(false)
      if (!resp.signup?.success) {
        return
      }

      let redirectUrl = `${ROOT_HOSTNAME}/events`
      if (event?.id) {
        redirectUrl = event.publicUrl
      } else if (collective?.id) {
        redirectUrl = collective.publicUrl
      }

      const token = resp.signup.jwtToken
      doLogin(token, {
        tracking: { method: 'Email+Password', type: 'signup' },
        redirectExplicit: redirectUrl
      })
    },
    [
      collective?.id,
      collective?.publicUrl,
      doLogin,
      event?.id,
      event?.publicUrl
    ]
  )

  const [signup, { loading: loadingPassword, error: errorPassword }] =
    useSignupMutation({
      variables: {
        signup: {
          email,
          password,
          username,
          deviceInfo,
          collectiveId: collective?.id,
          wallet,
          eventId: event?.id
        }
      },
      onCompleted: resp => {
        onAfterLogin(resp)
      },
      onError: () => {
        setLoggingIn(false)
        window.analytics?.trackAll(`Account - Signup Up - Error`, {
          type: 'Email+Password'
        })
      }
    })

  const errors = parseGqlErrors(errorPassword)
  const errorMessage =
    errors['signup.email'] ||
    errors['signup.password'] ||
    errors['signup.firstName'] ||
    errors['signup.lastName'] ||
    errors['signup.username'] ||
    errors._generic

  return {
    loading: loggingIn || loadingPassword,
    signup: async () => {
      setLoggingIn(true)
      await signup()
    },
    email,
    setEmail,
    password,
    setPassword,
    username,
    setRawUsername,
    errorMessage,
    setUsername,
    setLoggingIn
  }
}
